import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  collectHubRideActionContexts,
  ConfirmResult,
  HubRideActionKind,
  IRideDto,
  isValidPositiveNumber,
  useSystemParams,
} from "@ryde/shared";

import { useModalConfirm } from "../../../hooks-singletons";
import { buildHubRideConfirmMessage } from "./use-hub-ride-action-confirm.utils";

export const useHubRideActionConfirm = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const systemParams = useSystemParams();
  const isHubsLogicEnabled = Boolean(systemParams.enableHubsLogic);

  const confirmHubRideAction = useCallback(
    async ({
      rides,
      actionKind,
    }: {
      rides: (Partial<IRideDto> | undefined)[];
      actionKind: HubRideActionKind;
    }): Promise<boolean> => {
      if (!isHubsLogicEnabled) {
        return true;
      }

      const hubContexts = collectHubRideActionContexts(rides);

      if (!hubContexts.length) {
        // No hub-linked rides in selection — proceed without side-effect warning.
        return true;
      }

      const message = buildHubRideConfirmMessage({
        actionKind,
        hubContexts,
        t,
      });

      const confirmationResult = await confirmDialogResult({
        title: t("ride.HUB_RIDE_CONFIRM_TITLE"),
        message,
      });

      // proceed only when user confirms hub ride side effects
      return confirmationResult === ConfirmResult.OK;
    },
    [confirmDialogResult, isHubsLogicEnabled, t],
  );

  const confirmSelectedHubRidesAction = useCallback(
    async ({
      selectedRideIds,
      rides,
      actionKind,
    }: {
      selectedRideIds: number[];
      rides: (Partial<IRideDto> | undefined)[] | undefined;
      actionKind: HubRideActionKind;
    }): Promise<boolean> => {
      const selectedIdsSet = new Set(selectedRideIds);
      const selectedRides = (rides ?? []).filter(
        (ride): ride is Partial<IRideDto> =>
          isValidPositiveNumber(ride?.id) && selectedIdsSet.has(ride.id),
      );

      if (!selectedRides.length) {
        return true;
      }

      return confirmHubRideAction({ rides: selectedRides, actionKind });
    },
    [confirmHubRideAction],
  );

  return { confirmHubRideAction, confirmSelectedHubRidesAction };
};
